import { Head, Link, router } from '@inertiajs/react'

import { AreaTaxPayerType, AreaType, PaginationType } from './types.ts'
import {Activity, ArrowLeft, Briefcase, Edit, MapPin, Trash2, Users} from "lucide-react";
import Pagination from "@/components/pagination.tsx";

interface ShowProps {
  area: AreaType
  total_tax_payers?: number
  tax_payers?: AreaTaxPayerType[]
  tax_payers_pagination?: PaginationType
  total_collected_last_30_days?: number | string
  latest_payments?: Array<{
    id: string
    collected_at: string
    payment_method?: string
    tax_payer_name?: string
    agent_name?: string
    device_name?: string
    amount: number
  }>
  collect_sessions?: Array<{
    id: string
    code?: string
    status?: string
    start_at?: string
    end_at?: string | null
    agent_name?: string
    device_name?: string
  }>
  can_edit?: boolean
  can_destroy?: boolean
}

export default function Show({
  area,
  total_tax_payers = 0,
  tax_payers = [],
  tax_payers_pagination,
  total_collected_last_30_days = 0,
  latest_payments = [],
  collect_sessions = [],
  can_edit = false,
  can_destroy = false,
}: ShowProps) {

  const handleDestroy = () => {
    if (!window.confirm(`Supprimer la zone "${area.name}" ?`)) return

    router.delete(`/admin/areas/${area.id}`)
  }

  return (
    <>
      <Head title={`Zone #${area.id}`} />

      <div className="flex flex-col gap-6 p-8">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link href="/admin/areas">
              <button className="rounded-lg border border-border p-2 text-foreground hover:bg-secondary transition-colors">
                <ArrowLeft className="h-5 w-5" />
              </button>
            </Link>
            <div>
              <div className="flex items-center gap-3">
                <div className="rounded-lg bg-primary/10 p-3">
                  <MapPin className="h-6 w-6 text-primary" />
                </div>
                <div>
                  <h1 className="text-3xl font-bold text-foreground">{area.name}</h1>
                  <p className="text-sm text-muted-foreground">{area.code}</p>
                </div>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {can_edit && (
              <Link href={`/admin/areas/${area.id}/edit`}>
                <button className="flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 transition-colors">
                  <Edit className="h-4 w-4" />
                  Editer la zone
                </button>
              </Link>
            )}
            {can_destroy && (
              <button
                type="button"
                className="flex items-center gap-2 rounded-lg border border-destructive px-4 py-2 text-sm font-medium text-destructive hover:bg-destructive/10 transition-colors"
                onClick={handleDestroy}
              >
                <Trash2 className="h-4 w-4" />
                Supprimer
              </button>
            )}
          </div>
        </div>

        {/* Overview Stats */}
        <div className="grid gap-4 md:grid-cols-4">
          <div className="rounded-lg border border-border bg-card p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Les contribuables</p>
                <p className="mt-2 text-3xl font-bold text-foreground">{total_tax_payers}</p>
              </div>
              <Users className="h-8 w-8 text-info" />
            </div>
          </div>

          <div className="rounded-lg border border-border bg-card p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Collecte totale (30 jours)</p>
                <p className="mt-2 text-3xl font-bold text-success">
                  {Number(total_collected_last_30_days || 0).toFixed(2)} FCFA
                </p>
              </div>
            </div>
          </div>

          <div className="rounded-lg border border-border bg-card p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Agent assigné</p>
                <p className="mt-2 font-medium text-foreground">{area.assigned_agent_name || "Non assigné"}</p>
              </div>
              <Briefcase className="h-8 w-8 text-warning" />
            </div>
          </div>

          <div className="rounded-lg border border-border bg-card p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Statut</p>
                <p className="mt-2 text-lg font-semibold">
                  <span className="inline-flex rounded-full bg-success/10 px-3 py-1 text-xs font-medium text-success">Actif</span>
                </p>
              </div>
              <Activity className="h-8 w-8 text-success" />
            </div>
          </div>
        </div>

        <div className="grid gap-6 md:grid-cols-3">
          {/* Zone Information */}
          <div className="rounded-lg border border-border bg-card p-6 md:col-span-2">
            <h3 className="text-lg font-semibold text-foreground mb-4">Zone Information</h3>
            <div className="space-y-4">
              <div>
                <p className="text-xs font-medium text-muted-foreground uppercase">Description</p>
                <p className="mt-1 text-foreground">{area.description}</p>
              </div>
              <div>
                <p className="text-xs font-medium text-muted-foreground uppercase">Agent assigné</p>
                <p className="mt-1 text-foreground">{area.assigned_agent_name || "Non assigné"}</p>
              </div>
              <div>
                <p className="text-xs font-medium text-muted-foreground uppercase">Created</p>
                <p className="mt-1 text-foreground">{new Date(area.created_at).toLocaleDateString()}</p>
              </div>

            </div>
          </div>

          {/* Geographic Boundaries */}
          <div className="rounded-lg border border-border bg-card p-6">
            <h3 className="text-lg font-semibold text-foreground mb-4">Geographic Boundaries</h3>
            {/*<div className="space-y-3">*/}
            {/*  <div className="rounded-lg border border-border/50 bg-secondary p-3">*/}
            {/*    <p className="text-xs text-muted-foreground">North</p>*/}
            {/*    <p className="font-mono text-sm text-foreground">{areaDetails.boundaries.north}</p>*/}
            {/*  </div>*/}
            {/*  <div className="rounded-lg border border-border/50 bg-secondary p-3">*/}
            {/*    <p className="text-xs text-muted-foreground">South</p>*/}
            {/*    <p className="font-mono text-sm text-foreground">{areaDetails.boundaries.south}</p>*/}
            {/*  </div>*/}
            {/*  <div className="rounded-lg border border-border/50 bg-secondary p-3">*/}
            {/*    <p className="text-xs text-muted-foreground">East</p>*/}
            {/*    <p className="font-mono text-sm text-foreground">{areaDetails.boundaries.east}</p>*/}
            {/*  </div>*/}
            {/*  <div className="rounded-lg border border-border/50 bg-secondary p-3">*/}
            {/*    <p className="text-xs text-muted-foreground">West</p>*/}
            {/*    <p className="font-mono text-sm text-foreground">{areaDetails.boundaries.west}</p>*/}
            {/*  </div>*/}
            {/*</div>*/}
          </div>
        </div>

        <div className="rounded-lg border border-border bg-card p-6">
          <div className="mb-4 flex items-center justify-between">
            <h3 className="text-lg font-semibold text-foreground">Contribuables de la zone</h3>
            <p className="text-sm text-muted-foreground">{total_tax_payers} contribuable(s)</p>
          </div>

          {tax_payers.length === 0 ? (
            <div className="rounded-lg border border-dashed border-border p-4 text-sm text-muted-foreground">
              Aucun contribuable trouvé pour cette zone.
            </div>
          ) : (
            <div className="space-y-4">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="border-b border-border">
                    <tr className="text-left">
                      <th className="p-3 text-sm font-medium text-muted-foreground">Contribuable</th>
                      <th className="p-3 text-sm font-medium text-muted-foreground">Code</th>
                      <th className="p-3 text-sm font-medium text-muted-foreground">Type</th>
                      <th className="p-3 text-sm font-medium text-muted-foreground">Téléphone</th>
                      <th className="p-3 text-sm font-medium text-muted-foreground">Adresse</th>
                      <th className="p-3 text-sm font-medium text-muted-foreground">Solde</th>
                      <th className="p-3 text-sm font-medium text-muted-foreground">Statut</th>
                    </tr>
                  </thead>
                  <tbody>
                    {tax_payers.map((taxPayer) => (
                      <tr key={taxPayer.id} className="border-b border-border transition-colors hover:bg-secondary/50">
                        <td className="p-3">
                          <Link href={`/tax_payers/${taxPayer.id}`} className="font-medium text-primary hover:underline">
                            {taxPayer.name || "Contribuable sans nom"}
                          </Link>
                        </td>
                        <td className="p-3 text-sm text-foreground">{taxPayer.code || "-"}</td>
                        <td className="p-3 text-sm text-muted-foreground">{taxPayer.tax_payer_type_name || "-"}</td>
                        <td className="p-3 text-sm text-muted-foreground">{taxPayer.phone || "-"}</td>
                        <td className="p-3 text-sm text-muted-foreground">{taxPayer.address || "-"}</td>
                        <td className="p-3 text-sm font-semibold">
                          <span className={taxPayer.has_outstanding_balance ? "text-warning" : "text-success"}>
                            {Number(taxPayer.balance_due || 0).toFixed(2)}
                          </span>
                        </td>
                        <td className="p-3">
                          <span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-medium ${
                            taxPayer.has_outstanding_balance ? "bg-warning/10 text-warning" : "bg-success/10 text-success"
                          }`}>
                            {taxPayer.has_outstanding_balance ? "Outstanding" : "Cleared"}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {tax_payers_pagination && (
                <Pagination
                  currentPage={tax_payers_pagination.current_page}
                  totalPages={tax_payers_pagination.total_pages}
                  basePath={`/admin/areas/${area.id}`}
                  pageParam="tax_payers_page"
                />
              )}
            </div>
          )}
        </div>

        <div className="rounded-lg border border-border bg-card p-6">
          <div className="mb-4 flex items-center justify-between">
            <h3 className="text-lg font-semibold text-foreground">Derniers paiements</h3>
            <p className="text-sm text-muted-foreground">10 paiements les plus récents dans cette zone</p>
          </div>

          <div className="space-y-3">
            {latest_payments.length === 0 && (
              <div className="rounded-lg border border-dashed border-border p-4 text-sm text-muted-foreground">
                Aucun paiement trouvé pour cette zone.
              </div>
            )}

            {latest_payments.map((payment) => (
              <div key={payment.id} className="rounded-lg border border-border p-4">
                <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
                  <div>
                    <p className="font-medium text-foreground">{payment.tax_payer_name || "Contribuable inconnu"}</p>
                    <p className="text-sm text-muted-foreground">
                      Agent : {payment.agent_name || "Inconnu"} • Terminal : {payment.device_name || "Inconnu"}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {payment.payment_method || "cash"} • {new Date(payment.collected_at).toLocaleString()}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-lg font-bold text-success">{Number(payment.amount || 0).toFixed(2)} FCFA</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="rounded-lg border border-border bg-card p-6">
          <div className="mb-4 flex items-center justify-between">
            <h3 className="text-lg font-semibold text-foreground">Sessions de collecte</h3>
            <p className="text-sm text-muted-foreground">10 sessions les plus récentes pour cette zone</p>
          </div>

          <div className="space-y-3">
            {collect_sessions.length === 0 && (
              <div className="rounded-lg border border-dashed border-border p-4 text-sm text-muted-foreground">
                Aucune session de collecte trouvée pour cette zone.
              </div>
            )}

            {collect_sessions.map((session) => (
              <Link key={session.id} href={`/collect_sessions/${session.id}`}>
                <div className="rounded-lg border border-border p-4 transition-colors hover:bg-secondary/50">
                  <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
                    <div>
                      <p className="font-medium text-foreground">{session.code || `Session ${session.id}`}</p>
                      <p className="text-sm text-muted-foreground">
                        Agent : {session.agent_name || "Inconnu"} • Terminal : {session.device_name || "-"}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        Début: {session.start_at ? new Date(session.start_at).toLocaleString() : "-"}
                        {session.end_at ? ` • Fin: ${new Date(session.end_at).toLocaleString()}` : ""}
                      </p>
                    </div>
                    <span className="inline-flex rounded-full bg-secondary px-3 py-1 text-xs font-medium text-foreground">
                      {session.status || "-"}
                    </span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </div>
    </>
  )
}
